import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo } from "react";
import {
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { useMeCore } from "../../../cores/me";
import { useSocialPostCore } from "../../../cores/socialPost";
import { SocialProvider } from "../../../cores/socialPost/interfaces/socialProvider";
import { PanelWembii } from "../PanelWembii";
import { ableToPost } from "../socialPost/selector/SocialPostSelector";
import { ableToStory } from "../socialPost/selector/SocialStorySelector";
import { SocialTargetOptions } from "../../../cores/socialPost/config";
import { getUnifiedSocialCalendarPostRange } from "./socialCalendarFetchRange";

type CalendarView = "day" | "week" | "month";

interface Props {
  calendarView: CalendarView;
  onChangeCalendarView: (view: CalendarView) => void;
  bestHoursNetwork: "none" | "facebook" | "instagram" | "linkedin";
  onChangeBestHoursNetwork: (
    network: "none" | "facebook" | "instagram" | "linkedin"
  ) => void;
  showBestHoursPercent: boolean;
  onToggleBestHoursPercent: (value: boolean) => void;
  filterProvider: string;
  filterTarget: string;
  onFilterProviderChange: (id: string) => void;
  onFilterTargetChange: (value: string) => void;
}

export const SocialCalendarBar = ({
  calendarView,
  onChangeCalendarView,
  bestHoursNetwork,
  onChangeBestHoursNetwork,
  showBestHoursPercent,
  onToggleBestHoursPercent,
  filterProvider: provider,
  filterTarget: target,
  onFilterProviderChange: setProvider,
  onFilterTargetChange: setTarget,
}: Props) => {
  const { company, socialSettings } = useMeCore();
  const { date, getSocialPosts } = useSocialPostCore();

  const socialProviders: SocialProvider[] = useMemo(
    () =>
      (target === "post" ? ableToPost : ableToStory)
        .filter((social) => socialSettings![social])
        .map(
          (social): SocialProvider => ({
            id: social,
            name: social,
          })
        ),
    [socialSettings, target]
  );

  useEffect(() => {
    if (!company?.id) return;
    const { since, until } = getUnifiedSocialCalendarPostRange({
      calendarView,
      date,
    });
    getSocialPosts(company.id, since, until, provider, "", target);
  }, [company, date, provider, target, getSocialPosts, calendarView]);

  const onChangeTarget = useCallback(
    (nextTarget: string) => {
      setTarget(nextTarget);
      setProvider("all");
    },
    [setProvider, setTarget]
  );

  const providerOptions = useMemo(
    () => [
      { id: "all", label: "Todas", icon: ["far", "calendar-alt"] as any },
      ...socialProviders.map((item) => ({
        id: item.id,
        label: item.name,
        icon:
          item.id?.toLowerCase() === "facebook"
            ? (["fab", "facebook"] as any)
            : item.id?.toLowerCase() === "instagram"
            ? (["fab", "instagram"] as any)
            : item.id?.toLowerCase() === "linkedin"
            ? (["fab", "linkedin"] as any)
            : (["fas", "circle"] as any),
      })),
    ],
    [socialProviders]
  );

  const selectedProvider = useMemo(
    () => providerOptions.find((item) => item.id === provider) || providerOptions[0],
    [provider, providerOptions]
  );

  const bestHoursLabel = useMemo(() => {
    switch (bestHoursNetwork) {
      case "facebook":
        return "Facebook";
      case "instagram":
        return "Instagram";
      case "linkedin":
        return "LinkedIn";
      default:
        return "Mejores horas";
    }
  }, [bestHoursNetwork]);

  return (
    <PanelWembii
      className="social-calendar-bar social-calendar-bar--pro w-100 px-3 py-3 mb-3"
      shadow
      rounded="16px"
    >
      <Row className="social-calendar-bar__row align-items-end">
        <Col xs="12" lg="6" className="social-calendar-bar__col mb-2 mb-lg-0">
          <div className="social-calendar-bar__filters">
            <UncontrolledDropdown className="social-calendar-bar__dropdown social-calendar-bar__dropdown--provider">
              <DropdownToggle
                color="light"
                className="social-calendar-bar__dropdown-toggle w-100 d-flex align-items-center justify-content-between"
              >
                <span className="d-flex align-items-center min-width-0">
                  <FontAwesomeIcon
                    icon={selectedProvider.icon}
                    className="mr-2 social-calendar-bar__provider-icon"
                  />
                  <span className="text-truncate">
                    {selectedProvider.id === "all" ? "Filtro por red" : selectedProvider.label}
                  </span>
                </span>
                <FontAwesomeIcon icon={["fas", "chevron-down"]} size="sm" />
              </DropdownToggle>
              <DropdownMenu className="social-calendar-bar__menu w-100">
                {providerOptions.map((item) => (
                  <DropdownItem
                    key={item.id}
                    className="d-flex align-items-center justify-content-between"
                    onClick={() => setProvider(item.id)}
                  >
                    <span className="d-flex align-items-center text-capitalize">
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="mr-2 social-calendar-bar__provider-icon"
                      />
                      {item.label}
                    </span>
                    {provider === item.id && (
                      <FontAwesomeIcon icon={["fas", "check"]} className="text-primary" />
                    )}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
            <div className="social-calendar-bar__type-select">
              <UncontrolledDropdown className="social-calendar-bar__dropdown w-100">
                <DropdownToggle
                  color="light"
                  className="social-calendar-bar__dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                >
                  <span className="text-capitalize">{target}</span>
                  <FontAwesomeIcon icon={["fas", "chevron-down"]} size="sm" />
                </DropdownToggle>
                <DropdownMenu className="social-calendar-bar__menu w-100">
                  {SocialTargetOptions.map(({ value, label }) => (
                    <DropdownItem
                      key={value}
                      className="social-calendar-bar__menu-item d-flex align-items-center justify-content-between text-capitalize"
                      onClick={() => onChangeTarget(value)}
                    >
                      {label}
                      {target === value && (
                        <FontAwesomeIcon
                          icon={["fas", "check"]}
                          className="text-primary"
                        />
                      )}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </div>
        </Col>
        <Col
          xs="12"
          lg="6"
          className="social-calendar-bar__col d-flex justify-content-center justify-content-lg-end align-items-center"
        >
          <div className="social-calendar-bar__actions d-flex align-items-end">
            <UncontrolledDropdown className="social-calendar-bar__menu-trigger mr-0 mr-lg-2">
              <DropdownToggle
                color="light"
                className="social-calendar-bar__dropdown-toggle social-calendar-bar__best-hours-toggle d-flex align-items-center justify-content-between"
              >
                <span className="d-flex align-items-center">
                  <span className="social-calendar-bar__best-hours-icon mr-2">
                    <FontAwesomeIcon
                      icon={
                        bestHoursNetwork === "facebook"
                          ? (["fab", "facebook"] as any)
                          : bestHoursNetwork === "instagram"
                          ? (["fab", "instagram"] as any)
                          : bestHoursNetwork === "linkedin"
                          ? (["fab", "linkedin"] as any)
                          : (["far", "clock"] as any)
                      }
                    />
                  </span>
                  {bestHoursLabel}
                </span>
                <FontAwesomeIcon icon={["fas", "chevron-down"]} size="sm" />
              </DropdownToggle>
              <DropdownMenu right className="social-calendar-bar__menu">
                <DropdownItem
                  className="social-calendar-bar__menu-item d-flex align-items-center justify-content-between"
                  onClick={() => onChangeBestHoursNetwork("none")}
                >
                  <span className="d-flex align-items-center">
                    <span className="social-calendar-bar__provider-icon mr-2">
                      <FontAwesomeIcon icon={["far", "calendar-alt"]} />
                    </span>
                    Ninguna
                  </span>
                  {bestHoursNetwork === "none" && (
                    <FontAwesomeIcon icon={["fas", "check"]} className="text-primary" />
                  )}
                </DropdownItem>
                <DropdownItem
                  className="social-calendar-bar__menu-item d-flex align-items-center justify-content-between"
                  onClick={() => onChangeBestHoursNetwork("facebook")}
                >
                  <span className="d-flex align-items-center">
                    <span className="social-calendar-bar__provider-icon mr-2 text-facebook">
                      <FontAwesomeIcon icon={["fab", "facebook"]} />
                    </span>
                    Facebook
                  </span>
                  {bestHoursNetwork === "facebook" && (
                    <FontAwesomeIcon icon={["fas", "check"]} className="text-primary" />
                  )}
                </DropdownItem>
                <DropdownItem
                  className="social-calendar-bar__menu-item d-flex align-items-center justify-content-between"
                  onClick={() => onChangeBestHoursNetwork("instagram")}
                >
                  <span className="d-flex align-items-center">
                    <span className="social-calendar-bar__provider-icon mr-2 text-instagram">
                      <FontAwesomeIcon icon={["fab", "instagram"]} />
                    </span>
                    Instagram
                  </span>
                  {bestHoursNetwork === "instagram" && (
                    <FontAwesomeIcon icon={["fas", "check"]} className="text-primary" />
                  )}
                </DropdownItem>
                <DropdownItem
                  className="social-calendar-bar__menu-item d-flex align-items-center justify-content-between"
                  onClick={() => onChangeBestHoursNetwork("linkedin")}
                >
                  <span className="d-flex align-items-center">
                    <span className="social-calendar-bar__provider-icon mr-2 text-linkedin">
                      <FontAwesomeIcon icon={["fab", "linkedin"]} />
                    </span>
                    LinkedIn
                  </span>
                  {bestHoursNetwork === "linkedin" && (
                    <FontAwesomeIcon icon={["fas", "check"]} className="text-primary" />
                  )}
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem
                  toggle={false}
                  className="social-calendar-bar__menu-item d-flex align-items-center justify-content-between"
                  onClick={() => onToggleBestHoursPercent(!showBestHoursPercent)}
                >
                  Mostrar como porcentaje
                  <span
                    className={`social-calendar-bar__switch ${
                      showBestHoursPercent ? "is-on" : ""
                    }`}
                    aria-hidden
                  />
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            <UncontrolledDropdown className="social-calendar-bar__menu-trigger">
              <DropdownToggle
                color="light"
                className="social-calendar-bar__dropdown-toggle d-flex align-items-center justify-content-between"
              >
                <span>Vista del calendario</span>
                <FontAwesomeIcon icon={["fas", "chevron-down"]} size="sm" />
              </DropdownToggle>
              <DropdownMenu right className="social-calendar-bar__menu">
                <DropdownItem
                  className="d-flex align-items-center justify-content-between"
                  onClick={() => onChangeCalendarView("day")}
                >
                  Día
                  {calendarView === "day" && (
                    <FontAwesomeIcon icon={["fas", "check"]} className="text-primary" />
                  )}
                </DropdownItem>
                <DropdownItem
                  className="d-flex align-items-center justify-content-between"
                  onClick={() => onChangeCalendarView("week")}
                >
                  Semana
                  {calendarView === "week" && (
                    <FontAwesomeIcon icon={["fas", "check"]} className="text-primary" />
                  )}
                </DropdownItem>
                <DropdownItem
                  className="d-flex align-items-center justify-content-between"
                  onClick={() => onChangeCalendarView("month")}
                >
                  Mes
                  {calendarView === "month" && (
                    <FontAwesomeIcon icon={["fas", "check"]} className="text-primary" />
                  )}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </Col>
      </Row>
    </PanelWembii>
  );
};
